import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Star, Loader2, ChevronLeft, ChevronRight, MessageSquare, Shield, Clock, Zap } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';
import Layout from '@/components/layout/Layout';
import JsonLd from '@/components/common/JsonLd';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionHeader from '@/components/common/SectionHeader';
import { getServiceSchema, getProductWithReviewsSchema, getFAQSchema, getBreadcrumbSchema } from '@/lib/jsonld-schema';

interface ServiceDetail {
  id: string;
  name: string;
  description: string;
  category_id: string;
  estimated_hours: number;
  image_url: string;
}

interface GalleryImage {
  id: string;
  image_url: string;
  alt_text: string;
  caption: string;
  image_type: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

interface Review {
  id: string;
  rating: number;
  title: string;
  review_text: string;
  helpful_count: number;
  is_verified: boolean;
  created_at: string;
  customer_name: string;
}

interface ReviewStats {
  total_reviews: number;
  average_rating: number;
  verified_reviews: number;
  five_star_count: number;
  four_star_count: number;
  three_star_count: number;
  two_star_count: number;
  one_star_count: number;
}

interface RelatedService {
  id: string;
  name: string;
  description: string;
  price: string;
  image: string;
}

interface ServiceInclusion {
  id: string;
  inclusion_text: string;
  display_order: number;
}

interface ServiceExclusion {
  id: string;
  exclusion_text: string;
  display_order: number;
}

export default function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleRequestService = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      // Not logged in - go to service request page
      navigate('/services');
      return;
    }
    if (user.role === 'customer') {
      // Customer - go to customer dashboard request service
      navigate('/dashboard/request-service');
    } else if (user.role === 'admin' || user.role === 'provider') {
      // Admin/Provider - show alert, don't redirect to admin dashboard
      alert('Please log in as a customer to request a service.');
    } else {
      navigate('/services');
    }
  };
  const [service, setService] = useState<ServiceDetail | null>(null);
  const [gallery, setGallery] = useState<GalleryImage[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [inclusions, setInclusions] = useState<ServiceInclusion[]>([]);
  const [exclusions, setExclusions] = useState<ServiceExclusion[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<ReviewStats | null>(null);
  const [related, setRelated] = useState<RelatedService[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);
  
  // ============ PRIORITY 2: ADD CANONICAL URL ============
  useCanonicalUrl();

  useEffect(() => {
    if (!serviceId) return;

    const fetchServiceDetails = async () => {
      try {
        setLoading(true);

        // Fetch main service details
        const serviceRes = await fetch(config.getApiUrl(`/api/services/${serviceId}`));
        const serviceData = await serviceRes.json();
        if (serviceData.success && serviceData.data) {
          setService(serviceData.data);
        }

        // Fetch gallery
        const galleryRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/gallery`));
        const galleryData = await galleryRes.json();
        if (galleryData.success) {
          setGallery(galleryData.gallery || []);
        }

        // Fetch FAQs
        const faqRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/faqs`));
        const faqData = await faqRes.json();
        if (faqData.success) {
          setFaqs(faqData.faqs || []);
        }

        // AUDIT FIX: Fetch service inclusions (what IS included)
        const inclusionsRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/inclusions`));
        const inclusionsData = await inclusionsRes.json();
        if (inclusionsData.success) {
          setInclusions(inclusionsData.inclusions || []);
        }

        // AUDIT FIX: Fetch service exclusions (what is NOT included)
        const exclusionsRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/exclusions`));
        const exclusionsData = await exclusionsRes.json();
        if (exclusionsData.success) {
          setExclusions(exclusionsData.exclusions || []);
        }

        // Fetch reviews
        const reviewRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/reviews?limit=5`));
        const reviewData = await reviewRes.json();
        if (reviewData.success) {
          setReviewStats(reviewData.stats);
          setReviews(reviewData.reviews || []);
        }

        // Fetch related services
        const relatedRes = await fetch(config.getApiUrl(`/api/public/services/${serviceId}/related?limit=4`));
        const relatedData = await relatedRes.json();
        if (relatedData.success) {
          setRelated(relatedData.related || []);
        }
      } catch (error) {
        console.error('Error fetching service details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
  }, [serviceId]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!service) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <p className="text-center text-gray-600 mb-4">Service not found</p>
              <Link to="/services">
                <Button variant="outline" className="w-full">
                  Back to Services
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Render star rating
  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <Helmet>
        <title>{service.name} | Virewire</title>
        <meta name="description" content={service.description} />
        <meta property="og:title" content={service.name} />
        <meta property="og:description" content={service.description} />
        <meta property="og:type" content="website" />
        {service.image_url && <meta property="og:image" content={service.image_url} />}
      </Helmet>

      {/* JSON-LD Structured Data for SEO */}
      <JsonLd schema={getServiceSchema({
        name: service.name,
        description: service.description,
        image: service.image_url,
        price: 'Contact for pricing',
        priceCurrency: 'GBP',
        areaServed: ['London', 'Manchester', 'Birmingham', 'UK'],
        provider: 'Virewire'
      })} />

      {/* JSON-LD Product Schema with Reviews if ratings exist */}
      {reviewStats && reviewStats.total_reviews > 0 && (
        <JsonLd schema={getProductWithReviewsSchema({
          name: service.name,
          description: service.description,
          image: service.image_url,
          price: 'Contact for pricing',
          priceCurrency: 'GBP',
          ratingValue: reviewStats.average_rating,
          ratingCount: reviewStats.total_reviews,
          reviewCount: reviewStats.total_reviews
        })} />
      )}

      {/* JSON-LD FAQ Schema if FAQs exist */}
      {faqs.length > 0 && (
        <JsonLd schema={getFAQSchema(faqs.map(faq => ({
          question: faq.question,
          answer: faq.answer
        })))} />
      )}

      {/* JSON-LD Breadcrumb Schema */}
      <JsonLd schema={getBreadcrumbSchema([
        { name: 'Home', url: 'https://virewire.com' },
        { name: 'Services', url: 'https://virewire.com/services' },
        { name: service.name, url: window.location.href }
      ])} />

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600">Home</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-blue-600">Services</Link>
              <span>/</span>
              <span className="text-gray-900 font-medium">{service.name}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Left Column - Gallery & Details */}
            <div className="lg:col-span-2">
              {/* Image Gallery */}
              <Card className="mb-8 overflow-hidden">
                <div className="relative bg-gray-200 aspect-video flex items-center justify-center">
                  {gallery.length > 0 ? (
                    <>
                      <img
                        src={gallery[activeImageIndex].image_url}
                        alt={gallery[activeImageIndex].alt_text}
                        className="w-full h-full object-cover"
                      />
                      {gallery.length > 1 && (
                        <>
                          <button
                            onClick={() => setActiveImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length)}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded"
                          >
                            <ChevronLeft size={24} />
                          </button>
                          <button
                            onClick={() => setActiveImageIndex((prev) => (prev + 1) % gallery.length)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded"
                          >
                            <ChevronRight size={24} />
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {gallery.map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setActiveImageIndex(index)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  index === activeImageIndex ? 'bg-white w-8' : 'bg-white/50'
                                }`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-gray-500">
                      <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </Card>

              {/* Service Description */}
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="text-3xl mb-4">{service.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-gray-700 text-lg">{service.description}</p>

                  {/* Service Features */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 pt-6 border-t">
                    {service.estimated_hours && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-gray-600">Duration</p>
                          <p className="font-semibold">{service.estimated_hours}h</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">From</p>
                        <p className="font-semibold">£{price}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm text-gray-600">Quality</p>
                        <p className="font-semibold">Guaranteed</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="pt-6 border-t">
                    <Button size="lg" className="w-full md:w-auto" onClick={handleRequestService}>
                      Request Service
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Quick Info */}
            <div className="lg:col-span-1">
              {/* Pricing Card */}
              <Card className="mb-8 sticky top-8">
                <CardHeader>
                  <CardTitle className="text-2xl text-center">From ${price}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-center text-sm text-gray-600">
                    Final price depends on property size and specific requirements
                  </p>
                  <Button size="lg" className="w-full" onClick={handleRequestService}>
                    Request Service
                  </Button>
                  <Button variant="outline" size="lg" className="w-full">
                    Contact Us
                  </Button>
                </CardContent>
              </Card>

              {/* Reviews Summary Card */}
              {reviewStats && reviewStats.total_reviews > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Customer Reviews</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl font-bold">{reviewStats.average_rating.toFixed(1)}</div>
                      <div>
                        {renderStars(reviewStats.average_rating)}
                        <p className="text-sm text-gray-600 mt-1">{reviewStats.total_reviews} reviews</p>
                      </div>
                    </div>
                    
                    {/* Rating breakdown */}
                    <div className="space-y-2 border-t pt-4">
                      {[5, 4, 3, 2, 1].map((stars) => {
                        const count = reviewStats[`${stars}_star_count` as keyof ReviewStats] as number || 0;
                        const percentage = reviewStats.total_reviews > 0 ? (count / reviewStats.total_reviews) * 100 : 0;
                        return (
                          <div key={stars} className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 w-8">{stars}★</span>
                            <div className="flex-1 bg-gray-200 rounded h-2">
                              <div
                                className="bg-yellow-400 h-2 rounded transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <Tabs defaultValue="details" className="mb-12">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
              {(inclusions.length > 0 || exclusions.length > 0) && <TabsTrigger value="details">What's Included</TabsTrigger>}
              {faqs.length > 0 && <TabsTrigger value="faqs">FAQs</TabsTrigger>}
              {gallery.length > 0 && <TabsTrigger value="gallery">Gallery</TabsTrigger>}
              {reviews.length > 0 && <TabsTrigger value="reviews">Reviews</TabsTrigger>}
            </TabsList>

            {/* What's Included Tab - AUDIT FIX */}
            {(inclusions.length > 0 || exclusions.length > 0) && (
              <TabsContent value="details" className="mt-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Inclusions */}
                  {inclusions.length > 0 && (
                    <Card>
                      <CardHeader className="bg-green-50">
                        <CardTitle className="text-green-700 flex items-center gap-2">
                          <span className="text-2xl">✓</span>
                          What's Included
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <ul className="space-y-3">
                          {inclusions.map((inclusion) => (
                            <li key={inclusion.id} className="flex items-start gap-3">
                              <span className="text-green-600 font-bold mt-0.5">+</span>
                              <span className="text-gray-700">{inclusion.inclusion_text}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Exclusions */}
                  {exclusions.length > 0 && (
                    <Card>
                      <CardHeader className="bg-gray-50">
                        <CardTitle className="text-gray-700 flex items-center gap-2">
                          <span className="text-2xl">✕</span>
                          What's Not Included
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-6">
                        <ul className="space-y-3">
                          {exclusions.map((exclusion) => (
                            <li key={exclusion.id} className="flex items-start gap-3">
                              <span className="text-gray-600 font-bold mt-0.5">−</span>
                              <span className="text-gray-700">{exclusion.exclusion_text}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            )}

            {/* FAQs Tab */}
            {faqs.length > 0 && (
              <TabsContent value="faqs" className="mt-8">
                <div className="space-y-4">
                  {faqs.map((faq) => (
                    <Card key={faq.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent
                        className="p-6"
                        onClick={() => setExpandedFaqId(expandedFaqId === faq.id ? null : faq.id)}
                      >
                        <div className="flex items-start justify-between">
                          <h3 className="font-semibold text-gray-900 flex-1">{faq.question}</h3>
                          <span className="ml-4 text-blue-600">
                            {expandedFaqId === faq.id ? '−' : '+'}
                          </span>
                        </div>
                        {expandedFaqId === faq.id && (
                          <p className="mt-3 text-gray-600 text-sm">{faq.answer}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            )}

            {/* Gallery Tab */}
            {gallery.length > 0 && (
              <TabsContent value="gallery" className="mt-8">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {gallery.map((image, index) => (
                    <Card
                      key={image.id}
                      className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <div className="aspect-square">
                        <img
                          src={image.image_url}
                          alt={image.alt_text}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {image.caption && (
                        <CardContent className="pt-3">
                          <p className="text-sm text-gray-600">{image.caption}</p>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </TabsContent>
            )}

            {/* Reviews Tab */}
            {reviews.length > 0 && (
              <TabsContent value="reviews" className="mt-8">
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <Card key={review.id}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {renderStars(review.rating)}
                              {review.is_verified && (
                                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                  <Shield size={14} />
                                  Verified
                                </span>
                              )}
                            </div>
                            <p className="font-semibold">{review.title}</p>
                            <p className="text-sm text-gray-600">{review.customer_name}</p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700">{review.review_text}</p>
                        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                          <button className="flex items-center gap-1 hover:text-blue-600">
                            <MessageSquare size={16} />
                            Helpful ({review.helpful_count})
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>

          {/* Related Services */}
          {related.length > 0 && (
            <div>
              <SectionHeader
                title="Related Services"
                subtitle="You might also be interested in"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((relatedService) => (
                  <Link key={relatedService.id} to={`/service/${relatedService.id}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="aspect-square overflow-hidden bg-gray-200">
                        <img
                          src={relatedService.image}
                          alt={relatedService.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">
                          {relatedService.name}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {relatedService.description}
                        </p>
                        <p className="text-lg font-semibold text-blue-600">{relatedService.price}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
